import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

export interface IDofinPermissions {
  /** Admin, AccountManager, SalesManager, FinanceManager. */
  canRead: boolean;
  /** Admin, AccountManager, SalesManager. */
  canWrite: boolean;
}

/**
 * ТЗ 0024 — зеркало ролевых списков бэкенда
 * (`dofinansowanie/schema/schema.py`: DOFIN_READ_ROLES / DOFIN_MANAGER_ROLES).
 *
 * FinanceManager читает, но не пишет: ему нужно знать, кто финансируется
 * государством, при выставлении фактур, и ничего больше. Раньше его пускали
 * на полностью редактируемый экран, и любое действие оборачивалось молча
 * отклонённым промисом.
 *
 * `canRead` нужен ещё и чтобы НЕ отправлять запрос с чужой страницы: панель
 * дофинансирования висит на карточке студента, которую открывают роли без
 * доступа к домену, и запрос выдал бы ошибку прав поверх постороннего экрана.
 *
 * Скрытие контрола — это UX, а не защита: авторитет по каждой мутации
 * остаётся за бэкендом.
 */
/**
 * Зеркало `DOFIN_MANAGER_ROLES` бэкенда: кто пишет в разделе — и кто шлёт SMS
 * кандидату из его карточки (ТЗ 0084, `sendFundingCandidateSms`).
 */
export const DOFIN_MANAGER_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

export const useDofinPermissions = (): IDofinPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canWrite = DOFIN_MANAGER_ROLES.some((role) => roles.includes(role));

  return {
    canRead: canWrite || roles.includes(UserRole.FINANCIER),
    canWrite,
  };
};
